import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import styles from '../Login/css/Login.module.css';
import { logoutWithAudit, setActiveWorkspaceId, setWorkspaceSelectionRequired } from '../../redux/usersSlice';
import Logo from '../../assets/longLogoBright.png';
import { setProjectImg } from '../../redux/projectDataSlice';
import { setDocs } from '../../redux/documentsSlice';
import { getReturnPathFromLocation } from '../../utils/postAuthRedirect';

function getPathAfterWorkspaceSelect(membership, location) {
    const returnPath = getReturnPathFromLocation(location);
    if (returnPath) {
        return returnPath;
    }
    if (membership?.role === 'WORKSPACE_SUPER_ADMIN' && membership?.workspace?.is_setuped === false) {
        return `/workspace-setup/${membership.workspace.id}`;
    }
    return '/project';
}

const WorkspaceSelect = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const memberships = useSelector(state => state?.user?.memberships || []);
    const workspacesLoading = useSelector(state => state?.user?.workspacesLoading);
    const defaultWorkspaceId = useSelector(state => state?.user?.defaultWorkspaceId);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId);

    const selectedId = activeWorkspaceId || defaultWorkspaceId || '';

    React.useEffect(() => {
        if (!workspacesLoading && !memberships?.length) {
            navigate('/no-workspace-access', { replace: true });
            return;
        }

        if (memberships.length > 1) {
            dispatch(setWorkspaceSelectionRequired(true));
        }

        if (memberships.length === 1) {
            const m = memberships[0];
            const onlyWorkspaceId = m?.workspace?.id;
            if (onlyWorkspaceId) {
                dispatch(setActiveWorkspaceId(onlyWorkspaceId));
                dispatch(setWorkspaceSelectionRequired(false));
                navigate(getPathAfterWorkspaceSelect(m, location), { replace: true });
            }
        }
    }, [memberships, workspacesLoading, dispatch, navigate]);

    const handleLogout = async () => {
        localStorage.removeItem('persist:root');
        Cookies.set('refresh', '');
        dispatch(setProjectImg(''));
        dispatch(setDocs([]));
        dispatch(setActiveWorkspaceId(''));
        dispatch(setWorkspaceSelectionRequired(false));
        await dispatch(logoutWithAudit());
        navigate('/');
    };

    return (
        <div className={styles.body}>
            <div className={styles.dotedBg}>
                <div className={styles.formCard}>
                    <div className={styles.formHeader}>
                        <img src={Logo} width={73} alt="lens-logo" />
                    </div>
                    <div className={styles.formBody}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 32 }}>
                            <div className={styles.formLabel} style={{ marginBottom: 0 }}>
                                Multiple organisations are linked to this Lens account.
                            </div>
                            <div style={{ color: 'var(--Brand-Primary)', fontFamily: 'Inter', fontSize: 14, lineHeight: '120%' }}>
                                Please select one to continue.
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 32 }}>
                            {memberships.map(m => {
                                const id = m?.workspace?.id;
                                const name = m?.workspace?.name || 'Unnamed workspace';
                                const checked = selectedId === id;

                                return (
                                    <button
                                        key={m?.id || id}
                                        type="button"
                                        onClick={() => {
                                            dispatch(setActiveWorkspaceId(id));
                                            dispatch(setWorkspaceSelectionRequired(false));
                                            navigate(getPathAfterWorkspaceSelect(m, location), { replace: true });
                                        }}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            border: 'none',
                                            padding: 16,
                                            background: checked ? 'rgba(200, 151, 65, 0.05)' : '#F1F2F4',
                                            cursor: 'pointer',
                                            fontFamily: 'Inter',
                                            fontWeight: 700,
                                            fontSize: 18,
                                            lineHeight: '120%',
                                            color: 'var(--Brand-Primary)',
                                        }}
                                    >
                                        {name}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <button
                                type="button"
                                onClick={handleLogout}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    color: 'var(--Brand-Secondary)',
                                    fontFamily: 'Inter',
                                    fontSize: 14,
                                    lineHeight: '20px',
                                    cursor: 'pointer',
                                }}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                    Terms of Use
                </a>
                <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                    Privacy & Cookies
                </a>
            </div>
        </div>
    );
};

export default WorkspaceSelect;

